import { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import api from "../data/axios/api";

interface ApiRequestOptions {
  url: string;
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  payload?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  responseType?: "json" | "blob" | "text";
  skipToast?: boolean;
  sucessMesage?: string;
  errorMesage?: string;
  signal?: AbortSignal;
  /** Quando true, não altera `isLoading` — útil para revalidações em segundo
   * plano que não devem desmontar a UI (ex.: recarregar detail sem perder o
   * estado local do drawer). */
  silent?: boolean;
}

export const useApiRequest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const toast = useToast();

  const request = useCallback(async ({
    url,
    method = "GET",
    payload,
    params,
    headers,
    responseType = "json",
    skipToast = false,
    sucessMesage,
    errorMesage,
    signal,
    silent = false,
  }: ApiRequestOptions) => {

    if (!silent) setIsLoading(true);
    setError(null);

    try {
      const targetUrl = params
        ? `${url}?${new URLSearchParams(params).toString()}`
        : url;

      let body: any;
      let proxyEndpoint: string;
      let axiosConfig: Record<string, any> = {
        responseType: responseType === "blob" ? "blob" : "json",
        signal,
      };

      if (payload instanceof FormData) {
        payload.append("url", targetUrl);
        if (headers) payload.append("proxy_headers", JSON.stringify(headers));
        body = payload;
        proxyEndpoint = "/api/v1/proxy-form-data";
      } else {
        body = { url: targetUrl, method, payload, headers };
        proxyEndpoint = "/api/v1/proxy";
      }

      const response = await api.post(proxyEndpoint, body, axiosConfig);

      setData(response.data);

      if (!skipToast) {
        toast({
          title: sucessMesage ? sucessMesage :  "Operação concluída",
          status: "success",
          duration: 6000,
          isClosable: true,
        });
      }

      return response.data;

    } catch (err: any) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") throw err;

      const httpStatus = err?.response?.status;
      if (httpStatus !== 401) {
        Sentry.captureException(err, { extra: { url, method } });
      }

      setError(err);

      if (!skipToast) {
        toast({
          title: "Erro",
          description: errorMesage ? errorMesage : "Não foi possível completar a solicitação.",
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      }

      throw err;

    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [toast]);

  return {
    request,
    data,
    error,
    isLoading,
  };
};